import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/** The fixed v1 verbs a Computer-Use binding answers, called as `<prefix>.<verb>` under the binding's own prefix. */
export const COMPUTER_USE_ACTIONS = [
    'navigate',
    'click',
    'type',
    'screenshot',
    'extract',
    // os#679: files across the session boundary. Each is granted by name like the five above.
    'download',
    'upload',
    // os#1498: the model says it is on a sign-in page, and the run waits for a person to sign in. The OS does not
    // guess a login wall from the page: it is told.
    'sign_in',
    // #1648: the page read as its accessibility tree, with a reference on each element `click`, `type` and
    // `extract` can take in place of a selector. Granted by name like the rest.
    'snapshot',
    // #1649: hold until the page reaches a state (a URL, some text, an element visible, hidden or enabled), within
    // the operator's cap, so a redirect or a late form is checked when it lands rather than polled for.
    'wait',
    // #1650: choose a native dropdown's option by its exact visible label, and send a named key (Escape, Enter, Tab,
    // ArrowDown). `type` cannot set a `<select>`, and nothing else sends a key. Each is granted by name.
    'select',
    'press',
    // #1651: make another of the browser's open tabs (a popup, a `target="_blank"` page) the one every later verb acts
    // on, by a substring of its URL or title. Granted by name like the rest, and a bare prefix grants it not at all.
    'switch_tab',
    // #1654: move the pointer over an element, so a menu that only opens on hover opens, and bring an element or a
    // further part of the page into view, so content the page renders only once it is scrolled to is reachable.
    // Each is granted by name like the rest. Drag and drop is not offered.
    'hover',
    'scroll',
] as const

/** The directions a page `scroll` moves in (#1654). `down` when it is left out. */
export const COMPUTER_USE_SCROLL_DIRECTIONS = ['up', 'down', 'left', 'right'] as const

/** The states a `wait` can hold an element to (#1649). */
export const COMPUTER_USE_WAIT_STATES = ['visible', 'hidden', 'enabled'] as const

/**
 * The longest a `wait` holds, in milliseconds, unless the operator lowers it with the env setting of the same name
 * (#1649): three minutes. A `timeout` above it is clamped to it, never refused.
 */
export const COMPUTER_USE_WAIT_MAX_MS = 180_000

export type ComputerUseAction = (typeof COMPUTER_USE_ACTIONS)[number]

/** Stands for the binding's prefix in a Computer-Use tool's name and description. */
export const COMPUTER_USE_PREFIX = '<prefix>'

const BINDING =
    'A Computer-Use binding on this prefix (host-only allowed sites and a start URL), a model that can drive a browser, and a task that lists `<prefix>.<verb>` by name: a bare prefix grants no browser.'

const ERRORS: readonly BuiltInToolError[] = [
    {
        code: 'computer_use_model_unsupported',
        when: "The run's model is not one trusted to drive a browser. The run is blocked before any model call.",
        handling: 'fail',
    },
    {
        code: 'computer_use_browser_unavailable',
        when: 'No browser could be started for the run.',
        handling: 'fail',
    },
    {
        code: 'computer_use_site_blocked',
        when: "The page is on a host outside the binding's allowed sites.",
        handling: 'tool_error',
    },
    { code: 'computer_use_failed', when: 'The browser action itself failed.', handling: 'tool_error' },
]

/** A `ref` that no longer names what the latest snapshot showed (#1648): the page moved, or the element went. */
const STALE_REF: BuiltInToolError = {
    code: 'computer_use_stale_ref',
    when: 'The ref is not from the latest snapshot, or the page has changed since that snapshot. Nothing was done: snapshot again and use a reference from the new one.',
    handling: 'tool_error',
}

/** A `select` whose label the dropdown does not offer (#1650): the labels it does offer come back with it. */
const UNKNOWN_OPTION: BuiltInToolError = {
    code: 'computer_use_unknown_option',
    when: 'The dropdown offers no option whose visible label is exactly label. The error lists the labels it does offer, in the order it offers them. Nothing was chosen: the dropdown keeps the value it had.',
    handling: 'tool_error',
}

/**
 * A `switch_tab` no open tab answers (#1651): its `match` is in no tab's URL or title, or it gave none. The listing
 * of every open tab comes back with it, which is how a model finds the tabs: there is no separate verb to list them.
 */
const TAB_NOT_FOUND: BuiltInToolError = {
    code: 'computer_use_tab_not_found',
    when: "No open tab's URL or title contains match, or no match was given. The error lists every open tab by its title and host, the tab the run is on marked. Nothing changed: the run stays on that tab.",
    handling: 'tool_error',
}

/** The operator's cap on a wait, in the words the model, the reference and the docs all read (#1649). */
const WAIT_CAP = `the operator cap, COMPUTER_USE_WAIT_MAX_MS: ${COMPUTER_USE_WAIT_MAX_MS} ms (three minutes) unless the operator set it lower`

/**
 * The refusals only some verbs can meet, on top of the ones every verb can: the file verbs' (os#679), the
 * sign-in's (os#1498), those of the verbs that take a `ref` from a snapshot (#1648), a wait's (#1649), a
 * select's unknown option (#1650), and a tab switch that no tab answers (#1651). An unknown key name is the driver's
 * own refusal, `computer_use_failed`. A tab on a host the binding does not allow is `computer_use_site_blocked`, which
 * every verb can meet.
 */
const VERB_ERRORS: Partial<Record<ComputerUseAction, readonly BuiltInToolError[]>> = {
    click: [STALE_REF],
    type: [STALE_REF],
    extract: [STALE_REF],
    select: [UNKNOWN_OPTION, STALE_REF],
    press: [STALE_REF],
    switch_tab: [TAB_NOT_FOUND],
    hover: [STALE_REF],
    scroll: [STALE_REF],
    wait: [
        {
            code: 'computer_use_wait_timeout',
            when: `The page did not reach every condition asked for within the timeout, which is clamped to ${WAIT_CAP}. The error names the conditions, the timeout used and the page the browser is on. The browser stays open.`,
            handling: 'tool_error',
        },
        STALE_REF,
    ],
    snapshot: [
        {
            code: 'computer_use_snapshot_too_large',
            when: 'The tree is too large for one tool result. It is refused with its size, never cut short: scope it to one element with ref or selector, or make it shallower with depth.',
            handling: 'tool_error',
        },
        STALE_REF,
    ],
    download: [
        {
            code: 'computer_use_file_not_saved',
            when: 'The downloaded file could not be kept, for example because the artifact store refused its size.',
            handling: 'tool_error',
        },
    ],
    upload: [
        {
            code: 'computer_use_upload_refused',
            when: 'The upload named something that is not a file this run holds: a path, a URL, or a handle no file has.',
            handling: 'tool_error',
        },
    ],
    sign_in: [
        {
            code: 'computer_use_sign_in_not_completed',
            when: "Nobody signed in before the enterprise's gate timeout. The run is cancelled, and its record names the site.",
            handling: 'fail',
        },
    ],
}

const URL_FIELD: JsonSchema = {
    type: 'string',
    description: 'The full address to open. Its host must be one of the sites the binding allows.',
}

/** A selector, as the worker hands it to the browser. */
function selectorField(what: string): JsonSchema {
    return { type: 'string', description: `A CSS selector for the element ${what}.` }
}

/**
 * A reference from the latest snapshot (#1648), the alternative to a selector. The session resolves it, so it wins
 * when both are given, the way `download` documents `url` winning over `selector`.
 */
function refField(what: string): JsonSchema {
    return {
        type: 'string',
        description: `A reference the latest snapshot gave the element ${what}: e12 for [ref=e12]. Used instead of selector when both are given.`,
    }
}

/**
 * A read-only verb's ask for a frame in the run's step record (#1653), on `snapshot` and `wait` only: every other verb
 * always keeps its frame. Without it the step is recorded as its line alone. Only a literal true asks, and what the
 * model is told back does not change either way: frames are for the people watching the run.
 */
function frameField(verb: 'snapshot' | 'wait'): JsonSchema {
    const failed = verb === 'wait' ? ' A failed wait always keeps a frame, whatever this says.' : ''
    return {
        type: 'boolean',
        description: `true to keep a frame (a screenshot) of the page for this ${verb} in the run's step record, for a person watching the run. Left out, the ${verb} is recorded with no frame.${failed} What you are told back does not change.`,
    }
}

/**
 * The arguments each verb's browser action reads, by the names `browserActionRequestOf` (the worker's argument
 * mapper) looks for first. The mapper also takes `css` or `target` for `selector` and `value` for `text`, so every
 * schema stays open and a call spelled with an alias still reaches the browser.
 *
 * `required` tells the model what to pass. Nothing holds a call to it. The intercept judges a call only against a
 * schema a vendor's server listed (`servedSchemasOf` in `apps/api/src/worker/served-schemas.ts`), and nothing lists
 * these verbs. The worker's mapper checks nothing, and the model clients send no `strict` flag, so
 * `{ target: '#go' }` runs as a click. `apps/api/src/worker/computer-use-run.spec.ts` drives exactly that call
 * through a run. If anything starts enforcing these schemas, `required` has to name each alias too, or go.
 * `screenshot` reads nothing, so it declares no fields, which the reference says outright rather than as an open
 * object.
 *
 * `click` and `type` name their element by `ref` or by `selector` (#1648), so neither is required on its own, the
 * way neither of `download`'s `url` and `selector` is.
 */
const SCHEMAS: Record<ComputerUseAction, JsonSchema> = {
    navigate: { type: 'object', properties: { url: URL_FIELD }, required: ['url'], additionalProperties: true },
    click: {
        type: 'object',
        properties: { ref: refField('to click'), selector: selectorField('to click') },
        additionalProperties: true,
    },
    type: {
        type: 'object',
        properties: {
            ref: refField('to type into'),
            selector: selectorField('to type into'),
            text: { type: 'string', description: 'What to put in the element. It replaces what the element holds.' },
        },
        required: ['text'],
        additionalProperties: true,
    },
    screenshot: { type: 'object', properties: {}, additionalProperties: true },
    extract: {
        type: 'object',
        properties: { ref: refField('whose text to read'), selector: selectorField('whose text to read') },
        additionalProperties: true,
    },
    // os#679. A download takes either one: the driver fetches `url` when it is given, and otherwise clicks
    // `selector` and keeps the file that click starts. So neither is required on its own.
    download: {
        type: 'object',
        properties: {
            url: {
                type: 'string',
                description:
                    "The file's address. Its host, and every redirect's, must be one of the sites the binding allows. Used instead of selector when both are given.",
            },
            selector: selectorField('whose click starts the download'),
        },
        additionalProperties: true,
    },
    upload: {
        type: 'object',
        properties: {
            selector: selectorField('to attach the file to'),
            file: {
                type: 'string',
                description:
                    'The handle of a file this run holds: the id a download returned, or the handle of a file the task was given. A path or a URL is refused.',
            },
        },
        required: ['selector', 'file'],
        additionalProperties: true,
    },
    // os#1498. The site and the page are read off the browser, never off the call, so nothing is required.
    sign_in: {
        type: 'object',
        properties: {
            reason: {
                type: 'string',
                description: 'What on the page says a sign-in is needed, for the person who is asked to sign in.',
            },
        },
        additionalProperties: true,
    },
    // #1648. Every field narrows the read, so none is required: the whole page, every level, by default.
    snapshot: {
        type: 'object',
        properties: {
            ref: {
                type: 'string',
                description:
                    'A reference the latest snapshot gave an element: e12 for [ref=e12]. Reads only that element and what it contains. Used instead of selector when both are given.',
            },
            selector: selectorField('whose part of the page to read'),
            depth: {
                type: 'integer',
                minimum: 1,
                description:
                    'How many levels of the tree to keep, counted from the top, or from the element ref or selector names. Every level when it is left out.',
            },
            frame: frameField('snapshot'),
            // #1655. Only a literal true asks for a diff, as for frame.
            since: {
                type: 'boolean',
                description:
                    "true to get only what changed since the latest snapshot of this page, read with the same ref, selector and depth: the elements added, changed and removed, each anchored under its parent's reference. Every reference to an element still on the page keeps working, listed or not. With no earlier snapshot of this page to compare with (none yet, the page has moved, another tab, or another part of the page), it returns a full snapshot and says so.",
            },
        },
        additionalProperties: true,
    },
    // #1649. Each field is one condition, or narrows one, so none is required on its own; a call naming no
    // condition at all is refused by the session rather than waited out.
    wait: {
        type: 'object',
        properties: {
            url_contains: {
                type: 'string',
                description: "Text the address of the browser's page must contain, for a redirect to land.",
            },
            text: {
                type: 'string',
                description:
                    'Text that must be visible on the page, iframes included. Case and runs of whitespace are ignored.',
            },
            ref: refField('to wait on'),
            selector: selectorField('to wait on'),
            state: {
                type: 'string',
                enum: COMPUTER_USE_WAIT_STATES,
                description: 'The state the element ref or selector names must reach. visible when it is left out.',
            },
            timeout: {
                type: 'integer',
                description: `How long to wait, in milliseconds. Left out, it is ${WAIT_CAP}; a longer one is clamped to that.`,
            },
            frame: frameField('wait'),
        },
        additionalProperties: true,
    },
    // #1650. The dropdown is named by ref or by selector, so neither is required on its own, like click's. The option
    // is named by its label only (CTO decision 1): a snapshot shows labels, not values, so there is no value field.
    select: {
        type: 'object',
        properties: {
            ref: refField('to choose an option in'),
            selector: selectorField('to choose an option in'),
            label: {
                type: 'string',
                description:
                    'The visible label of the option to choose, exactly as the dropdown shows it. Where two options share it, the first is chosen.',
            },
        },
        required: ['label'],
        additionalProperties: true,
    },
    // #1650. The key goes to whatever has focus, or to the element ref or selector names, focused first.
    press: {
        type: 'object',
        properties: {
            key: {
                type: 'string',
                description:
                    'The name of the key to press: Escape, Enter, Tab, ArrowDown, ArrowUp, Backspace, Space and the like.',
            },
            ref: refField('to focus before the key is pressed'),
            selector: selectorField('to focus before the key is pressed'),
        },
        required: ['key'],
        additionalProperties: true,
    },
    // #1651. Left out, the call is refused with the listing of the open tabs, which is how a model finds them.
    switch_tab: {
        type: 'object',
        properties: {
            match: {
                type: 'string',
                description:
                    "A piece of the tab's URL or title, case ignored: pay.example.org, or PayPal. Where more than one tab has it, the one opened first is chosen.",
            },
        },
        additionalProperties: true,
    },
    // #1654. The element is named by ref or by selector, so neither is required on its own, like click's.
    hover: {
        type: 'object',
        properties: { ref: refField('to move the pointer over'), selector: selectorField('to move the pointer over') },
        additionalProperties: true,
    },
    // #1654. Either an element to bring into view (ref or selector, which then wins over direction and amount), or a
    // page scroll by direction and amount. Every field has a default or an alternative, so none is required: a bare
    // scroll is one viewport down.
    scroll: {
        type: 'object',
        properties: {
            ref: refField('to bring into view'),
            selector: selectorField('to bring into view'),
            direction: {
                type: 'string',
                enum: COMPUTER_USE_SCROLL_DIRECTIONS,
                description:
                    'Which way to scroll the page: up, down, left or right. down when it is left out. Ignored when ref or selector names an element.',
            },
            amount: {
                type: 'integer',
                minimum: 1,
                description:
                    'How far to scroll the page, in CSS pixels. One viewport when it is left out. Ignored when ref or selector names an element.',
            },
        },
        additionalProperties: true,
    },
}

const VERBS: Record<
    ComputerUseAction,
    Pick<BuiltInTool, 'summary' | 'description' | 'effect' | 'retry' | 'retryNote' | 'returns'>
> = {
    navigate: {
        summary: "Open `url` in the run's browser.",
        description:
            "Open a page in this run's browser. Pass url, the full address, on one of the sites the <prefix> binding allows: any other host is refused. Returns the address the browser reached, after any redirect.",
        effect: 'read',
        retry: 'safe',
        returns: 'The page reached.',
    },
    click: {
        summary: 'Click the element `ref` or `selector` names.',
        description:
            "Click one element on the page open in this run's browser. Pass ref, the reference the latest snapshot gave it, or selector, a CSS selector for it; ref is used when both are given. Returns the address the browser is on afterwards.",
        effect: 'write',
        retry: 'unsafe',
    },
    type: {
        summary: 'Type `text` into the element `ref` or `selector` names.',
        description:
            "Type into one field on the page open in this run's browser. Pass ref, the reference the latest snapshot gave the field, or selector, a CSS selector for it; ref is used when both are given. Pass text, which replaces what the field holds. Returns the address the browser is on afterwards.",
        effect: 'write',
        retry: 'unsafe',
    },
    screenshot: {
        summary: 'Take a screenshot of the page.',
        description:
            "Take a screenshot of the page open in this run's browser. Takes no arguments. The screenshot is reported by its size, not pasted into the conversation.",
        effect: 'read',
        retry: 'safe',
        returns: 'The screenshot, reported by size rather than pasted into the conversation.',
    },
    extract: {
        summary: 'Read the text of the element `ref` or `selector` names.',
        description:
            "Read the text of one element on the page open in this run's browser. Pass ref, the reference the latest snapshot gave it, or selector, a CSS selector for it; ref is used when both are given. Every credential the run used is taken out of the text before you read it.",
        effect: 'read',
        retry: 'safe',
        returns: 'The text, with every credential the run used taken out.',
    },
    download: {
        summary:
            'Download the file the site offers at `url`, or behind the control `selector` names, and keep it as a run file a person can take from the run.',
        description:
            "Download a file from the site open in this run's browser and keep it as a run file. Pass url, the file's address on one of the sites the <prefix> binding allows, or selector, a CSS selector for the control whose click starts the download. url is used when both are given, and every redirect is checked against the allowed sites. Returns the run file's handle (id), name, size and content type, and the URL it came from, never its bytes.",
        effect: 'read',
        retry: 'safe',
        returns:
            "The run file's handle (`id`), name, size and content type, and the URL it came from. Never its bytes.",
    },
    upload: {
        summary:
            'Attach a run file, named by the handle (`file`) a download or the task gave it, to the file input `selector` names. Waits for a person to approve it first.',
        description:
            "Attach a file this run holds to a file input on the page open in this run's browser. Pass selector, a CSS selector for the file input, and file, the handle of a run file: the id a download returned, or the handle of a file the task was given. A path or a URL is refused. A person approves every upload before the file is attached. Returns the address the browser is on afterwards.",
        effect: 'write',
        retry: 'unsafe',
        returns: 'The page the file was attached on.',
    },
    sign_in: {
        summary:
            "Stop at the sign-in page the browser is on and wait for a person to sign in. Their sign-in is saved for the run's member on that site.",
        description:
            "Call this when the page open in this run's browser asks you to sign in. Never type a username or password yourself. The run stops and waits for a person to take the browser, sign in and hand it back. Then you carry on from the page they left it on. What they typed is never shown to you. Pass reason, what on the page says a sign-in is needed. Returns the address the browser is on once the person has handed it back.",
        effect: 'read',
        retry: 'safe',
        returns: 'The page the browser is on after the person signed in.',
    },
    snapshot: {
        summary:
            'Read the page as its accessibility tree, with a reference such as `[ref=e12]` on each element `click`, `type` and `extract` can take as `ref`.',
        description:
            "Read the page open in this run's browser as its accessibility tree: one line per element with its role and name, and a reference such as [ref=e12] on each element you can act on, iframe content included under its iframe. Pass that reference as ref to click, type or extract instead of a selector. Only the latest snapshot's references work, and once the page has changed you snapshot again. Pass ref or selector to read only that element and what it contains, ref is used when both are given, and depth to keep only that many levels. A snapshot too large for one tool result is refused with its size, never cut short: scope it or make it shallower. Every credential the run used is taken out of the text. Pass since as true to get only what changed since the latest snapshot of this page, read with the same ref, selector and depth: the elements added, changed and removed, each anchored under its parent's reference, or a line saying nothing changed. After it, every reference to an element still on the page works, listed or not. When there is no earlier snapshot of this page to compare with, because there is none yet, the page has moved, you switched tabs or you asked for another part of the page, it returns a full snapshot and says so. Returns the page's title and address, then the tree.",
        effect: 'read',
        retry: 'safe',
        returns:
            "The page's title and address, then the accessibility tree with a reference on each element you can act on, with every credential the run used taken out. With since, only what changed since the latest snapshot of this page, or a full snapshot that says why when there is none to compare with.",
    },
    wait: {
        summary:
            'Wait until the page reaches a state: its address contains `url_contains`, `text` is visible, or the element `ref` or `selector` names is `state`, within `timeout`.',
        description:
            "Wait until the page open in this run's browser reaches a state, then return. Pass url_contains, text the page's address must contain; text, text that must be visible on the page, iframes included, ignoring case and runs of whitespace; or ref or selector with state (visible, hidden or enabled), an element that must reach that state, visible when state is left out; ref is used when both are given. When you pass more than one, all of them must hold before it returns, and a call naming none is refused. Pass timeout, in milliseconds, to wait less than " +
            `${WAIT_CAP}: left out it is that cap, and a longer one is clamped to it, never refused. ` +
            'A wait that runs out is the tool error computer_use_wait_timeout, naming what it waited for, the timeout it used and the page it ended on; the browser stays open and you carry on. It does not wait for network idle or for a download. If the page moved while you waited, references from your last snapshot no longer work: snapshot again. Returns the address the browser is on and the conditions that held.',
        effect: 'read',
        retry: 'safe',
        returns:
            'The address the browser is on, and the conditions that held with the timeout used. No accessibility tree.',
    },
    select: {
        summary: 'Choose the option labelled `label` in the dropdown `ref` or `selector` names.',
        description:
            "Choose an option in one native dropdown (a select element) on the page open in this run's browser. Pass ref, the reference the latest snapshot gave the dropdown, or selector, a CSS selector for it; ref is used when both are given. Pass label, the option's visible label exactly as the dropdown shows it: the label only, there is no value argument. A label the dropdown does not offer is the tool error computer_use_unknown_option, which lists the labels it does offer, and the dropdown keeps the value it had. Choosing an option that moves the browser off the sites the <prefix> binding allows is refused, as a click would be. Returns the address the browser is on afterwards.",
        effect: 'write',
        retry: 'idempotent',
        returns: 'The address the browser is on afterwards.',
    },
    press: {
        summary: 'Press the key `key`, on the element `ref` or `selector` names or on whatever has focus.',
        description:
            "Press one key on the page open in this run's browser. Pass key, the name of the key: Escape, Enter, Tab, ArrowDown, ArrowUp, Backspace, Space and the like. It goes to whatever has focus; pass ref, the reference the latest snapshot gave an element, or selector, a CSS selector for it, to focus that element first; ref is used when both are given. A key name the browser does not know is refused. A key that moves the browser off the sites the <prefix> binding allows is refused, as a click would be, and once the page has moved, snapshot again before using a reference. Returns the address the browser is on afterwards.",
        effect: 'write',
        retry: 'unsafe',
        returns: 'The address the browser is on afterwards.',
    },
    switch_tab: {
        summary:
            'Make the open tab whose URL or title contains `match` the one every later verb acts on: a popup or a page the site opened in a new tab.',
        description:
            "Switch to another of the tabs open in this run's browser: a page the site opened in a new tab or a popup, such as a payment provider's sign-in or a 3-D Secure challenge, or back to the page you came from. Pass match, a substring of the tab's URL or title, case ignored; where more than one tab has it, the one opened first is chosen. Every later verb acts on that tab until you switch again. A tab on a host the <prefix> binding does not allow is refused, and you stay on the tab you were on. A match no tab has, or no match at all, is the tool error computer_use_tab_not_found, which lists every open tab by its title and host. References from an earlier snapshot do not work on the tab you switch to: snapshot it first. If the tab you switched to closes itself, the next verb acts on the tab you switched from. Returns the URL of the tab you are now on, and its title.",
        effect: 'read',
        retry: 'safe',
        returns: 'The URL of the tab the run is now on, and its title.',
    },
    hover: {
        summary: 'Move the pointer over the element `ref` or `selector` names, so a menu that opens on hover opens.',
        description:
            "Move the pointer over one element on the page open in this run's browser, the way a person hovers it, so a menu or a tooltip that only appears on hover opens. Pass ref, the reference the latest snapshot gave it, or selector, a CSS selector for it; ref is used when both are given. Snapshot afterwards to see what appeared. A hover that moves the browser off the sites the <prefix> binding allows is refused, as a click would be. Returns the address the browser is on afterwards.",
        effect: 'read',
        retry: 'safe',
        returns: 'The address the browser is on afterwards.',
    },
    scroll: {
        summary:
            'Bring the element `ref` or `selector` names into view, or scroll the page `amount` pixels in `direction`.',
        description:
            "Scroll the page open in this run's browser, so content it renders only once it is scrolled to appears. Pass ref, the reference the latest snapshot gave an element, or selector, a CSS selector for it, to bring that element into the viewport; ref is used when both are given, and direction and amount are then ignored. Otherwise pass direction (up, down, left or right; down when left out) and amount, how far in CSS pixels (one viewport when left out), to scroll the page itself: a bare scroll is one viewport down. Any other direction, or an amount that is not a positive number, is refused and nothing scrolls. A scroll that moves the browser off the sites the <prefix> binding allows is refused, as a click would be. Snapshot afterwards to see what appeared. Returns the address the browser is on afterwards.",
        effect: 'read',
        retry: 'unsafe',
        retryNote:
            'A page scroll (direction and amount) moves the page again. Bringing an element into view (ref or selector) leaves one already in view where it is.',
        returns: 'The address the browser is on afterwards.',
    },
}

function computerUseTool(verb: ComputerUseAction): BuiltInTool {
    return {
        name: `${COMPUTER_USE_PREFIX}.${verb}`,
        family: 'computer-use',
        ...VERBS[verb],
        inputSchema: SCHEMAS[verb],
        callers: ['run'],
        needs: { binding: BINDING },
        errors: [...ERRORS, ...(VERB_ERRORS[verb] ?? [])],
        public: true,
    }
}

/** A browser a run drives through a Computer-Use binding, for a product with no MCP server. */
export function computerUseTools(): BuiltInTool[] {
    return COMPUTER_USE_ACTIONS.map(computerUseTool)
}

/**
 * The registered verb a `<prefix>.<verb>` call names, with the binding's own prefix in its name and description,
 * or undefined when `name` is not a verb under a prefix. Which prefixes are Computer-Use bindings is the run's to
 * know, not the registry's: this only reads the shape of the name.
 */
export function computerUseToolAs(name: string): BuiltInTool | undefined {
    const dot = name.indexOf('.')
    const prefix = name.slice(0, dot)
    const verb = name.slice(dot + 1).toLowerCase()
    if (dot <= 0 || !(COMPUTER_USE_ACTIONS as readonly string[]).includes(verb)) {
        return undefined
    }
    const tool = computerUseTool(verb as ComputerUseAction)
    return {
        ...tool,
        name: `${prefix}.${verb}`,
        description: tool.description.replaceAll(COMPUTER_USE_PREFIX, prefix),
    }
}
